import React, { useState, useEffect } from 'react';
import './FlashcardStudy.css';

const FlashcardStudy = ({ onBack }) => {
  const [flashcards, setFlashcards] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [wrongCards, setWrongCards] = useState([]);
  const [studyMode, setStudyMode] = useState('normal'); // 'normal' or 'review'

  useEffect(() => {
    loadFlashcards();
  }, []);

  const loadFlashcards = async () => {
    try {
      const response = await fetch('/flashcards.json');
      const data = await response.json();
      setFlashcards(data);
      setCategories(data.map(cat => cat.category));
      setLoading(false);
    } catch (error) {
      console.error('Error loading flashcards:', error);
      setLoading(false);
    }
  };

  const selectCategory = (category) => {
    setCurrentCategory(category);
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setStudyMode('normal');
  };

  const getCurrentCards = () => {
    if (!currentCategory) return [];
    
    if (studyMode === 'review') {
      return wrongCards;
    }
    
    const categoryData = flashcards.find(cat => cat.category === currentCategory);
    return categoryData ? categoryData.cards : [];
  };

  const currentCards = getCurrentCards();
  const currentCard = currentCards[currentCardIndex];

  const nextCard = () => {
    if (currentCardIndex < currentCards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setIsFlipped(false);
    }
  };

  const prevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
      setIsFlipped(false);
    }
  };

  const markAsWrong = () => {
    if (currentCard && studyMode === 'normal') {
      const cardWithCategory = { ...currentCard, category: currentCategory };
      setWrongCards(prev => {
        const exists = prev.some(card => 
          card.front === currentCard.front && card.category === currentCategory
        );
        if (!exists) {
          return [...prev, cardWithCategory];
        }
        return prev;
      });
    }
    nextCard();
  };

  const markAsCorrect = () => {
    if (studyMode === 'review' && currentCard) {
      setWrongCards(prev => prev.filter((card, index) => index !== currentCardIndex));
      if (currentCardIndex >= wrongCards.length - 1) {
        setCurrentCardIndex(Math.max(0, wrongCards.length - 2));
      }
    } else {
      nextCard();
    }
  };

  const startReviewMode = () => {
    if (wrongCards.length > 0) {
      setStudyMode('review');
      setCurrentCardIndex(0);
      setIsFlipped(false);
    }
  };

  const resetStudy = () => {
    setCurrentCategory(null);
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setWrongCards([]);
    setStudyMode('normal');
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return '#28a745';
      case 'Medium': return '#ffc107';
      case 'Hard': return '#dc3545';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div className="flashcard-container">
        <div className="loading">Loading flashcards...</div>
      </div>
    );
  }

  if (!currentCategory) {
    return (
      <div className="flashcard-container">
        <div className="flashcard-header">
          <button className="back-button" onClick={onBack}>
            ← Back to Menu
          </button>
          <h2>📚 Flashcard Study</h2>
          <p>Choose a category to start studying</p>
        </div>

        <div className="category-grid">
          {categories.map((category) => {
            const categoryData = flashcards.find(cat => cat.category === category);
            const cardCount = categoryData ? categoryData.cards.length : 0;
            
            return (
              <div 
                key={category}
                className="category-card"
                onClick={() => selectCategory(category)}
              >
                <h3>{category}</h3>
                <p>{cardCount} cards</p>
                <div className="category-preview">
                  {categoryData && categoryData.cards.slice(0, 3).map((card, index) => (
                    <div 
                      key={index}
                      className="difficulty-dot"
                      style={{ backgroundColor: getDifficultyColor(card.difficulty) }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {wrongCards.length > 0 && (
          <div className="review-section">
            <h3>🔄 Review Mode</h3>
            <p>You have {wrongCards.length} cards to review</p>
            <button className="review-button" onClick={startReviewMode}>
              Review Missed Cards
            </button>
          </div>
        )}
      </div>
    );
  }

  if (currentCards.length === 0) {
    return (
      <div className="flashcard-container">
        <div className="flashcard-header">
          <button className="back-button" onClick={() => setCurrentCategory(null)}>
            ← Back to Categories
          </button>
          <h2>No cards available</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="flashcard-container">
      <div className="flashcard-header">
        <button className="back-button" onClick={() => setCurrentCategory(null)}>
          ← Back to Categories
        </button>
        <h2>
          {studyMode === 'review' ? '🔄 Review Mode' : `📚 ${currentCategory}`}
        </h2>
        <div className="progress-info">
          Card {currentCardIndex + 1} of {currentCards.length}
          {studyMode === 'normal' && wrongCards.length > 0 && (
            <span className="wrong-count"> • {wrongCards.length} to review</span>
          )}
        </div>
      </div>

      <div className="flashcard-study">
        <div className={`flashcard ${isFlipped ? 'flipped' : ''}`}>
          <div className="flashcard-inner">
            <div className="flashcard-front">
              <div className="card-content">
                <h3>{currentCard.front}</h3>
                <div className="flip-hint">Click to reveal answer</div>
              </div>
              <div 
                className="difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(currentCard.difficulty) }}
              >
                {currentCard.difficulty}
              </div>
            </div>
            <div className="flashcard-back">
              <div className="card-content">
                <h3>Answer:</h3>
                <p>{currentCard.back}</p>
              </div>
              <div 
                className="difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(currentCard.difficulty) }}
              >
                {currentCard.difficulty}
              </div>
            </div>
          </div>
        </div>

        <div className="flashcard-controls">
          <button 
            className="nav-button prev"
            onClick={prevCard}
            disabled={currentCardIndex === 0}
          >
            ← Previous
          </button>

          <button 
            className="flip-button"
            onClick={() => setIsFlipped(!isFlipped)}
          >
            {isFlipped ? 'Show Question' : 'Show Answer'}
          </button>

          <button 
            className="nav-button next"
            onClick={nextCard}
            disabled={currentCardIndex === currentCards.length - 1}
          >
            Next →
          </button>
        </div>

        {isFlipped && (
          <div className="answer-feedback">
            <p>Did you get it right?</p>
            <div className="feedback-buttons">
              <button className="wrong-button" onClick={markAsWrong}>
                ❌ Need to Review
              </button>
              <button className="correct-button" onClick={markAsCorrect}>
                ✅ Got it Right
              </button>
            </div>
          </div>
        )}

        <div className="study-actions">
          <button className="reset-button" onClick={resetStudy}>
            🔄 Reset Study Session
          </button>
          {wrongCards.length > 0 && studyMode === 'normal' && (
            <button className="review-button" onClick={startReviewMode}>
              📝 Review {wrongCards.length} Missed Cards
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlashcardStudy; 